import { useCallback, useState } from "react";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  cancelPricebookSchedule,
  getPricebookSchedules,
  readPricebookDraftRefusal,
} from "../../../../services";
import { PricebookSchedule } from "../../../../types/price-book.type";
import ConfirmModal from "../../../ConfirmModal";
import CataloguePagination from "../../shared/Pagination";
import { useSearchablePagedRows } from "../../shared/useSearchablePagedRows";
import ScheduleCard from "../Schedules/ScheduleCard";
import { formatScheduleEffectiveText } from "../Schedules/scheduleModel";

const CANCEL_FAILED = "ยกเลิกการตั้งเวลาไม่สำเร็จ กรุณาลองอีกครั้ง";

interface SchedulesTabProps {
  pricebookUuid: string;
  /** May cancel a schedule waiting for its day (a catalogue writer). */
  canCancel: boolean;
  /** A schedule was cancelled, so the book's header (its bar and chip) has to be read again. */
  onChanged: () => void;
}

/**
 * ตั้งเวลา: the book's draft applies set for a later day, newest effective day first. The job
 * applies each at 00:01 Bangkok on its day by itself; nothing here runs one.
 */
export default function SchedulesTab({
  pricebookUuid,
  canCancel,
  onChanged,
}: SchedulesTabProps) {
  const [cancelTarget, setCancelTarget] = useState<PricebookSchedule | null>(null);
  const [cancelling, setCancelling] = useState<boolean>(false);
  const [cancelError, setCancelError] = useState<string>("");

  const fetchSchedules = useCallback(
    async (page: number, limit: number) => {
      const {
        data: { data },
      } = await getPricebookSchedules(pricebookUuid, page, limit);
      return data ?? { rows: [], count: 0 };
    },
    [pricebookUuid],
  );

  const { page, setPage, limit, setLimit, rows, totalCount, loading, reload } =
    useSearchablePagedRows<PricebookSchedule>({ open: true, fetchRows: fetchSchedules });

  const cancelDescription = cancelTarget
    ? `ราคาจากร่าง ${cancelTarget.draft_code} ที่ตั้งไว้ ${formatScheduleEffectiveText(
        cancelTarget.effective_date,
      )} จะไม่ถูกนำไปใช้ สมุดนี้ใช้ราคาเดิมต่อไป`
    : "";

  const handleAskCancel = (schedule: PricebookSchedule) => {
    setCancelError("");
    setCancelTarget(schedule);
  };
  const handleCloseCancel = () => {
    if (!cancelling) setCancelTarget(null);
  };
  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    setCancelError("");
    try {
      await cancelPricebookSchedule(cancelTarget.uuid);
      setCancelTarget(null);
      reload();
      onChanged();
    } catch (error) {
      const refusal = readPricebookDraftRefusal(error);
      setCancelError(refusal?.message ?? CANCEL_FAILED);
      // Refused because the job applied or refused it meanwhile: the card shows where it stands.
      if (refusal) reload();
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Stack gap={2}>
      <Typography variant="body2" color="text.secondary">
        ระบบนำราคาที่ตั้งเวลาไว้ไปใช้เองตอน 00:01 เวลาไทยของวันมีผล
        ตั้งเวลาได้จากปุ่มนำร่างไปใช้บนหน้าร่าง
      </Typography>
      {loading && (
        <Stack alignItems="center" sx={{ py: 5 }}>
          <CircularProgress />
        </Stack>
      )}
      {!loading && rows.length === 0 && (
        <Typography color="text.secondary" fontWeight={500} align="center" sx={{ py: 5 }}>
          ยังไม่มีการตั้งเวลา
        </Typography>
      )}
      {!loading &&
        rows.map((schedule) => (
          <ScheduleCard
            key={schedule.uuid}
            schedule={schedule}
            canCancel={canCancel}
            onCancel={handleAskCancel}
          />
        ))}
      <CataloguePagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />

      <ConfirmModal
        open={Boolean(cancelTarget)}
        title="ยกเลิกการตั้งเวลานี้?"
        description={cancelDescription}
        cancelLabel="ไม่ยกเลิก"
        confirmLabel="ยกเลิกการตั้งเวลา"
        confirmColor="error"
        loading={cancelling}
        onClose={handleCloseCancel}
        onConfirm={handleConfirmCancel}
      >
        {cancelError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {cancelError}
          </Alert>
        )}
      </ConfirmModal>
    </Stack>
  );
}
